import React, { useState } from 'react';
import { CalendarClock, MapPin, User, CheckCircle2, HelpCircle } from 'lucide-react';
import { parseLocalDateTime, formatTime, formatDateMedium } from '../../utils/timeUtils';
import type { RoutyAppointmentPickerArtifact, AppointmentPickChoice } from '../../business/routy/routyArtifacts';

interface AppointmentPickerCardProps {
  artifact: RoutyAppointmentPickerArtifact;
  disabled?: boolean;
  onPick: (artifact: RoutyAppointmentPickerArtifact, candidate: AppointmentPickChoice) => void;
  onNone: (artifact: RoutyAppointmentPickerArtifact) => void;
}

const AppointmentPickerCard: React.FC<AppointmentPickerCardProps> = ({ artifact, disabled, onPick, onNone }) => {
  const [resolved, setResolved] = useState(false);
  const locked = disabled || resolved;

  if (artifact.candidates.length === 0) return null;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <HelpCircle className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">{artifact.prompt || 'Do you mean this one?'}</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.candidates.map((cand) => {
          const start = parseLocalDateTime(cand.date, cand.startTime);
          const end = cand.endTime ? parseLocalDateTime(cand.date, cand.endTime) : null;
          return (
            <div key={cand.appointmentId} className="rounded-lg border border-ink-200 bg-surface p-3">
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-ink-900 truncate">{cand.title}</span>
              </div>
              {cand.contactName && (
                <p className="mt-0.5 pl-5 text-xs text-ink-500">{cand.contactName}</p>
              )}
              <div className="mt-1.5 flex items-start gap-2 text-xs text-ink-600">
                <CalendarClock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-400" />
                <span>
                  {start ? formatDateMedium(start) : cand.date}
                  {start ? ` · ${formatTime(start)}` : ` · ${cand.startTime}`}
                  {end ? ` – ${formatTime(end)}` : ''}
                </span>
              </div>
              {cand.address && (
                <div className="mt-1 flex items-start gap-2 text-xs text-ink-500">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-400" />
                  <span className="truncate">{cand.address}</span>
                </div>
              )}
              <button
                onClick={() => {
                  setResolved(true);
                  onPick(artifact, cand);
                }}
                disabled={locked}
                className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Yes, this one
              </button>
            </div>
          );
        })}

        {artifact.allowNone && (
          <button
            onClick={() => {
              setResolved(true);
              onNone(artifact);
            }}
            disabled={locked}
            className="w-full py-2 rounded-lg border border-ink-200 text-xs font-medium text-ink-600 hover:bg-ink-50 transition-colors disabled:opacity-50"
          >
            None of these
          </button>
        )}
      </div>
    </div>
  );
};

export default AppointmentPickerCard;
